export const ACTIVE_CONSULTATION_STATUSES = ['pending_review', 'matched', 'scheduled'];
export const HISTORICAL_SPECIALIST_STATUSES = ['matched', 'scheduled', 'completed'];
export const DEFAULT_COMMUNICATION_METHOD = 'video';

const PRIVATE_CONSULTATION_KEYS = ['status_history', 'admin_notes', 'cancelled_by_id', 'cancelled_by_type', 'actor_id'];
const PRIVATE_SPECIALIST_KEYS = ['avatar_path', 'is_active', 'phone', 'email', 'status', 'name_ar', 'name_en', 'title_ar', 'title_en', 'bio_ar', 'bio_en'];

export function isActiveConsultationStatus(status) {
  return ACTIVE_CONSULTATION_STATUSES.includes(status);
}

export function hasCompletedAttempt(home) {
  return Boolean(home?.latest_result?.attempt_id);
}

export function canRequestConsultation(home) {
  return hasCompletedAttempt(home) && !isActiveConsultationStatus(home?.consultation?.status);
}

export function consultationFocusRefetch(status) {
  if (!status || isActiveConsultationStatus(status)) {
    return 'always';
  }
  return false;
}

export function consultationDetailQueryOptions(status) {
  const active = !status || isActiveConsultationStatus(status);
  return {
    refetchOnWindowFocus: consultationFocusRefetch(status),
    refetchOnReconnect: consultationFocusRefetch(status),
    staleTime: active ? 0 : 60_000,
  };
}

export function shouldPresentAssignedSpecialist(consultation) {
  if (!consultation) {
    return false;
  }
  if (consultation.specialist && typeof consultation.specialist === 'object') {
    return true;
  }
  return HISTORICAL_SPECIALIST_STATUSES.includes(consultation.status);
}

export function specialistInitials(name) {
  const text = String(name || '').trim();
  if (!text) {
    return '?';
  }
  return Array.from(text)[0];
}

export function omitKeys(value, keys) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const next = { ...value };
  keys.forEach((key) => {
    delete next[key];
  });
  return next;
}

export function publicSpecialistView(specialist) {
  if (!specialist || typeof specialist !== 'object') {
    return null;
  }
  const sanitized = omitKeys(specialist, PRIVATE_SPECIALIST_KEYS);
  return {
    id: sanitized.id,
    public_key: sanitized.public_key,
    name: sanitized.name,
    title: sanitized.title,
    bio: sanitized.bio,
    languages: Array.isArray(sanitized.languages) ? sanitized.languages : [],
    timezone: sanitized.timezone,
    avatar_url: sanitized.avatar_url,
  };
}

export function publicConsultationView(consultation) {
  if (!consultation || typeof consultation !== 'object') {
    return {};
  }
  const sanitized = omitKeys(consultation, PRIVATE_CONSULTATION_KEYS);
  return {
    ...sanitized,
    specialist: publicSpecialistView(sanitized.specialist),
  };
}

export function userConsultationPayload(body = {}) {
  return {
    goal: body.goal,
    question: body.question,
    preferred_period: body.preferred_period,
    attempt_id: body.attempt_id,
    share_report_consent: body.share_report_consent,
    communication_method: body.communication_method || DEFAULT_COMMUNICATION_METHOD,
    idempotency_key: body.idempotency_key,
  };
}

export function withDefaultCommunicationMethods(methods, videoLabel = 'Video') {
  const list = Array.isArray(methods) ? methods.filter((item) => item && item.value) : [];
  if (!list.some((item) => item.value === DEFAULT_COMMUNICATION_METHOD)) {
    return [{ value: DEFAULT_COMMUNICATION_METHOD, label: videoLabel }, ...list];
  }
  return list;
}
